import React, { memo, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { SafeSVG } from "../svg/SafeSVG";
import { DropDownButton } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import "./DropDownButton.scss";

/**
 * @typedef {Object} items
 * @property {string} text - Text
 * @property {string} icon - Icon
 */

/**
 * Dropdown button
 * @module
 * @param {string} text - Button text
 * @param {items[]} items - Items to choose from
 * @param {string} [className] - ClassName of DropDownButton
 * @param {string} [buttonClassName] - ButtonClassName
 * @param {string} [icon] - Icon name
 * @param {string} [imageUrl] - Image URL
 * @param {func} onClick - Function invoked on button click
 * @param {bool} [disabled] - Disabled button
 * @param {object} [popupSettings] - Popup settings
 * @param {func} [itemRender] - itemRender function
 * @param {string} title - Button tooltip
 * @param {func} [customTooltipContent] - Custom tooltip content
 */
const DropDownButtonComponent = (props) => {
  const ref = useRef(null);
  const refMap = useRef(new Map());
  const propsItemRender = props.itemRender;
  const propsOnClick = props.onClick;
  const dropDownRef = useRef(null);

  useEffect(() => {
    if (dropDownRef.current && dropDownRef.current.element) {
      dropDownRef.current.element.setAttribute('data-testid', props.name || props.title);
    }
  }, [props.name, props.title])

  const getRefByKey = (key) => {
    let r = refMap.current.get(key);
    if (!r) {
      r = React.createRef();
      refMap.current.set(key, r);
    }
    return r;
  };

  const onClick = useCallback((e) => {
    if (e?.item?.disableClick) {
      e.syntheticEvent.preventDefault?.();
      e.syntheticEvent.stopPropagation?.();
      e.nativeEvent?.stopImmediatePropagation?.();
      return;
    }
    if (e.itemIndex !== undefined) {
      if (e?.item?.onItemClick) {
        e.item.onItemClick(e.itemIndex, e.item);
        return;
      }

      const div = document.body.querySelector(
        "div.k-animation-container.k-animation-container-fixed.k-animation-container-shown"
      );
      if (div) {
        div.style.display = "none";
      }

      propsOnClick(e.itemIndex, e.item.text);
    }
  }, [propsOnClick]);

  const onOpen = useCallback((e) => {
    setTimeout(() => {
      const div = document.body.querySelector(".k-animation-container.k-animation-container-relative > .k-popup");
      if (div) {
        const rec = div.parentElement.getBoundingClientRect();
        if (rec.right > window.innerWidth) {
          div.parentElement.style.left = (rec.left - rec.right + window.innerWidth) + "px";
        }
      }
    }, 100);
  }, []);

  const itemRender = useCallback((itemProps) => {
    if (propsItemRender) {
      return propsItemRender(itemProps);
    }

    const itemRef = getRefByKey(itemProps.item.index);
    return (
      <div
        className="drop-down-button-item-tooltip"
      >
        <Tooltip
          anchorElement="target"
          position="top"
          openDelay={0}
          targetElement={itemRef.current}
        >
          <div className="drop-down-button-item" title={itemProps.item.title} ref={itemRef} >
            {itemProps.item.imageUrl ? (
              <SafeSVG className="drop-down-button-item-SVG" src={itemProps.item.imageUrl} />
            ) : (
              <span
                className={
                  itemProps.item.iconClass
                    ? itemProps.item.iconClass
                    : `k-icon k-font-icon k-i-${itemProps.item.icon}`
                }
                role="presentation"
              />
            )}
            {itemProps.item.text}
          </div>
        </Tooltip>
      </div>
    );
  }, [propsItemRender]);

  return (
    <div className="dropdown-button">
      <Tooltip
        anchorElement="target"
        content={
          props.customTooltipContent ? () => props.customTooltipContent : null
        }
        position="top"
        openDelay={0}
        targetElement={ref.current}
      >
        <div
          ref={ref}
          title={props.title}
        >
          <DropDownButton
            text={props.text}
            items={props.items.map((x, index) => ((x.disableClick === undefined && x.disabled !== undefined) ? { ...x, disableClick: x.disabled, index: index } : { ...x, index: index }))}
            className={props.className}
            buttonClass={`${props.className || ""} ${props.imageUrl || props.icon ? props.text ? "icon-text-button" : "icon-button" : ""}`}
            icon={props.icon}
            startIcon={<SafeSVG src={props.imageUrl} />}
            fillMode={props.fillMode || props.look ? props.fillMode || props.look : props.icon || props.imageUrl ? "flat" : "solid"}
            onItemClick={onClick}
            onOpen={onOpen}
            disabled={props.disabled}
            popupSettings={props.popupSettings}
            itemRender={itemRender}
            themeColor={props.primary ? "primary" : "base"}
            ariaLabel={props.text || props.title}
            ref={dropDownRef}
          />
        </div>
      </Tooltip>
    </div>
  );
};

DropDownButtonComponent.propTypes = {
  text: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      icon: PropTypes.string,
    })
  ).isRequired,
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  icon: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  popupSettings: PropTypes.object,
  itemRender: PropTypes.func,
  title: PropTypes.string,
  customTooltipContent: PropTypes.object,
  imageUrl: PropTypes.string,
};

export default memo(DropDownButtonComponent);
